import { prepareFonts } from "./native-files";
import { clipboardSource, clipboardSourceFile } from "./clipboard-source";
import { clipboardImages } from "./clipboard-images";
import type { EngineDocument, Point } from "../scene/model";
import type { NativeSession } from "../native/session";
import {
  captureClipboard,
  clipboardMarker,
  clipboardType,
  pasteClipboard,
  readClipboard,
} from "../native/clipboard";
import type { ImageStore } from "../render/image-store";
import { imageMediaType } from "../native/image-formats";

function typing(target: EventTarget | null) {
  return (
    target instanceof Element &&
    !!target.closest("input,textarea,select,[contenteditable=true]")
  );
}
/** Browser events only: no background clipboard polling or permission prompts. */
export class NativeClipboard {
  prepareCopy?: () => void;
  private copied: string | null = null;
  private pasteEvent = 0;
  private operation = 0;
  constructor(
    private session: NativeSession,
    private images: ImageStore,
    private blocked: () => boolean,
    private center: () => Point,
    private applied: () => void,
    private message: (text: string, error?: boolean) => void,
  ) {
    document.addEventListener("copy", (event) => {
      if (!this.allowed(event.target) || !event.clipboardData) return;
      const copied = this.copy();
      if (!copied) return;
      event.clipboardData.setData(clipboardType, copied);
      event.clipboardData.setData("text/plain", clipboardMarker);
      event.preventDefault();
    });
    document.addEventListener("paste", (event) => {
      // Every real paste suppresses the shortcut fallback, even for external text/images.
      this.pasteEvent++;
      this.operation++;
      if (!this.allowed(event.target)) return;
      const payload = event.clipboardData?.getData(clipboardType);
      const ownMarker =
        event.clipboardData?.getData("text/plain") === clipboardMarker;
      const text = payload || (ownMarker ? this.copied : null);
      if (text) {
        event.preventDefault();
        void this.paste(text);
        return;
      }
      const files = Array.from(event.clipboardData?.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(
          (file): file is File =>
            !!file &&
            (file.type.startsWith("image/") ||
              !!imageMediaType(file.type, file.name)),
        );
      if (files.length) {
        event.preventDefault();
        void this.paste(clipboardImages(files, this.images, this.center()));
        return;
      }
      const source = clipboardSource(event.clipboardData);
      if (!source) return;
      event.preventDefault();
      const center = this.center();
      void this.paste(
        clipboardSourceFile(source).then((file) =>
          clipboardImages([file], this.images, center),
        ),
      );
    });
    window.addEventListener("keydown", (event) => {
      if (
        !(event.ctrlKey || event.metaKey) ||
        event.altKey ||
        !this.allowed(event.target)
      )
        return;
      if (event.code === "KeyC") this.copy();
      if (event.code === "KeyV") {
        const observed = this.pasteEvent,
          text = this.copied,
          revision = session.revision,
          generation = session.generation;
        setTimeout(() => {
          if (
            text &&
            this.pasteEvent === observed &&
            this.allowed(document.activeElement) &&
            revision === session.revision &&
            generation === session.generation
          )
            void this.paste(text);
        }, 80);
      }
    });
  }
  setContents(document: EngineDocument, ids: readonly string[]) {
    this.copied = captureClipboard(document, ids);
    this.operation++;
    void navigator.clipboard?.writeText(clipboardMarker).catch(() => undefined);
  }
  private allowed(target: EventTarget | null) {
    return (
      !this.blocked() && !typing(target) && !typing(document.activeElement)
    );
  }
  private copy() {
    this.prepareCopy?.();
    const text = captureClipboard(
      this.session.document,
      this.session.selectedIds,
    );
    if (text) {
      this.copied = text;
      this.operation++;
    }
    return text;
  }
  private async paste(text: string | Promise<EngineDocument>) {
    const token = ++this.operation,
      generation = this.session.generation,
      revision = this.session.revision,
      selection = JSON.stringify(this.session.selectedIds);
    try {
      const source =
        typeof text === "string" ? readClipboard(text) : await text;
      await this.images.prepare(source.assets);
      await prepareFonts(source);
      if (token !== this.operation) return;
      if (
        !this.allowed(document.activeElement) ||
        generation !== this.session.generation ||
        revision !== this.session.revision ||
        selection !== JSON.stringify(this.session.selectedIds)
      ) {
        this.message(
          "За время чтения буфера вы начали другое действие. Повторите вставку.",
          true,
        );
        return;
      }
      const result = pasteClipboard(
        this.session.document,
        source,
        typeof text === "string" ? this.center() : null,
        () => crypto.randomUUID(),
      );
      this.session.document = result.document;
      this.session.select(result.ids);
      this.session.finish();
      if (typeof text === "string")
        this.copied = captureClipboard(result.document, result.ids);
      this.applied();
      this.message(
        typeof text === "string"
          ? "Объекты вставлены."
          : "Изображение вставлено.",
      );
    } catch (error) {
      if (token === this.operation)
        this.message((error as Error).message, true);
    }
  }
}
